import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs/internal/Subscription';
import { KpiReportFilter } from 'src/app/common/dtos/reports.dto';
import { translateKpiReportFilterFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import { checkIfB2b, checkIfE2L } from 'src/app/common/utilities/role-helpers';
import { ReportPageFiltersService } from '../../shared/services/report/report-page-filters.service';
import { ReportService } from '../../shared/services/report/report.service';
import { ReportingPlansService } from '../../shared/services/report/reporting-plans.service';
import { FetchLastUpdatedDate } from '../../shared/state/reporting/reporting.actions';
import { ReportingState } from '../../shared/state/reporting/reporting.state';
import {
  ReportingImplementationPlansItemAPIResponse,
  ReportingImplementationPlansPhaseItemAPIResponse,
} from '../../shared/types/responses/reporting.responses';

const BASE_URL = EnvironmentService.apiUrl();
@Component({
  selector: 'app-reporting-plans',
  standalone: false,
  templateUrl: './reporting-plans.component.html',
  styleUrl: './reporting-plans.component.scss',
})
export class ReportingPlansComponent implements OnInit, OnDestroy {
  @ViewChild('p') csvDownloadingPopover: NgbPopover;

  subs: Subscription[] = [];

  canFilterByDistrict = false;

  isLoading = false;

  disableDownload = false;

  tableData: ReportingImplementationPlansItemAPIResponse[];

  listMeta: PaginationDTO;

  filters: KpiReportFilter[];

  lastUpdatedDate: string;

  noResults = false;

  constructor(
    private store: Store,
    private reportService: ReportService,
    private reportingPlansService: ReportingPlansService,
    private reportFiltersService: ReportPageFiltersService
  ) {
    const lastUpdatedDate$ = this.store.select(ReportingState.getUpdatedDate);
    this.subs.push(
      lastUpdatedDate$.subscribe((date) => {
        this.lastUpdatedDate = date;
      })
    );

    const user = this.store.selectSnapshot(UserState.getUser) as User;
    this.canFilterByDistrict = checkIfE2L(user) || checkIfB2b(user);
  }

  ngOnInit() {
    this.subs.push(
      this.reportFiltersService.filters.pipe().subscribe((filters) => {
        if (filters.schoolYearFilter) {
          this.filters = translateKpiReportFilterFromFilterDTO(filters);
          this.getPlans(this.filters);
          this.store.dispatch(new FetchLastUpdatedDate());
        }
      })
    );
  }

  getPlans(filters: KpiReportFilter[], page?: number) {
    this.isLoading = true;

    this.reportingPlansService.getPlans(filters, page).subscribe((res) => {
      this.tableData = res.items;
      this.listMeta = res._meta;
      this.isLoading = false;
    });
  }

  downloadCSV(): void {
    this.disableDownload = true;

    this.reportService
      .generateReportCSV('implementationPlanDownload', this.filters)
      .subscribe({
        error: () => {
          this.csvDownloadingPopover.close();
          this.noResults = true;
          this.disableDownload = false;
        },
        next: (res) => {
          if (res.has_results) {
            // res.file_name should always start with a slash, but need to be sure for the link to work properly
            if (!res.file_name.startsWith('/')) {
              res.file_name = `/${res.file_name}`;
            }
            window.open(`${BASE_URL}${res.file_name}`, '_blank');
          } else {
            this.noResults = true;
          }
          this.disableDownload = false;
        },
      });
  }

  paginationChange() {
    this.getPlans(this.filters, this.listMeta.currentPage);
  }

  getUniquePhases(): { phase_name: string; phase_num: number }[] {
    if (!this.tableData) return [];

    const phaseMap = new Map<number, string>();

    this.tableData.forEach((plan) => {
      plan.phases?.forEach((phase) => {
        phaseMap.set(phase.phase_num, phase.phase_name);
      });
    });

    return Array.from(phaseMap.entries())
      .map(([phase_num, phase_name]) => ({ phase_num, phase_name }))
      .sort((a, b) => a.phase_num - b.phase_num);
  }

  // eslint-disable-next-line class-methods-use-this
  getPhaseData(
    plan: ReportingImplementationPlansItemAPIResponse,
    phaseNum: number
  ) {
    return plan.phases?.find((phase) => phase.phase_num === phaseNum);
  }

  // eslint-disable-next-line class-methods-use-this
  trackByPhase(
    index: number,
    phase: { phase_name: string; phase_num: number }
  ): number {
    return phase.phase_num;
  }

  getColumnCount(): number {
    return 1 + this.getUniquePhases().length * 2; // 1 for plan name + 2 columns per phase
  }

  // eslint-disable-next-line class-methods-use-this
  getPhaseBackgroundColor(
    phaseData: ReportingImplementationPlansPhaseItemAPIResponse
  ): string {
    if (!phaseData?.phase_progress_fraction) return 'transparent';

    const percentage = phaseData.phase_progress_percent * 100;

    const stops = [
      { pct: 0, r: 240, g: 238, b: 233 },
      { pct: 25, r: 220, g: 214, b: 197 },
      { pct: 50, r: 177, g: 216, b: 238 },
      { pct: 75, r: 124, g: 185, b: 218 },
      { pct: 100, r: 88, g: 163, b: 203 },
    ];

    // Find the two stops to interpolate between
    let lowerStop = stops[0];
    let upperStop = stops[stops.length - 1];

    for (let i = 0; i < stops.length - 1; i += 1) {
      if (percentage >= stops[i].pct && percentage <= stops[i + 1].pct) {
        lowerStop = stops[i];
        upperStop = stops[i + 1];
        break;
      }
    }

    // Interpolate between the two stops
    const range = upperStop.pct - lowerStop.pct;
    const factor = range === 0 ? 0 : (percentage - lowerStop.pct) / range;

    const r = Math.round(lowerStop.r + (upperStop.r - lowerStop.r) * factor);
    const g = Math.round(lowerStop.g + (upperStop.g - lowerStop.g) * factor);
    const b = Math.round(lowerStop.b + (upperStop.b - lowerStop.b) * factor);

    return `rgb(${r}, ${g}, ${b})`;
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
